// Pure helpers — visual only. Never mutates leads.
//
// Reads the AI-generated quote text on a lead and extracts a numeric price.
// Mirrors the backend parser (see _extract_customer_price in server.py):
//   - Prefers "Suggested Price: $X"
//   - Falls back to "Price Range: $low – $high" (uses midpoint)
//   - Returns null if nothing parsable is found.
//
// Only counts leads with open statuses: new, contacted, quoted.
// Booked / closed / cancelled leads are excluded.

const OPEN_STATUSES = new Set(["new", "contacted", "quoted"]);
const PRICE_NUM_RE = /\$?\s*([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]+)?|[0-9]+(?:\.[0-9]+)?)/g;

// Per-service-type default used when a lead has no parseable AI quote yet.
// Keeps the Potential Revenue card meaningful for brand-new users who haven't
// generated quotes. Values match the product spec; keys are normalized to
// a loose lowercase form so "Deep Clean", "deep-cleaning", "Deep cleaning"
// all hit the same bucket.
const SERVICE_TYPE_DEFAULTS = {
  "standard": 150,
  "standardcleaning": 150,
  "regularcleaning": 150,
  "deep": 300,
  "deepcleaning": 300,
  "moveout": 350,
  "moveoutcleaning": 350,
  "movein": 350,
  "moveincleaning": 350,
  "moveinout": 350,
  "airbnb": 180,
  "airbnbcleaning": 180,
  "shortterm": 180,
  "shorttermrental": 180,
  "recurring": 200,
  "recurringcleaning": 200,
  "weekly": 200,
  "biweekly": 200,
  "monthly": 200,
  "office": 220,
  "officecleaning": 220,
  "commercial": 220,
};
const SERVICE_DEFAULT_FALLBACK = 200;

const normalizeServiceKey = (v) =>
  String(v || "").toLowerCase().replace(/[^a-z]/g, "");

export const estimateServiceTypePrice = (serviceType) => {
  const key = normalizeServiceKey(serviceType);
  if (!key) return SERVICE_DEFAULT_FALLBACK;
  return SERVICE_TYPE_DEFAULTS[key] ?? SERVICE_DEFAULT_FALLBACK;
};

const toNumber = (raw) => {
  if (raw == null) return null;
  const cleaned = String(raw).replace(/,/g, "").trim();
  const n = parseFloat(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const parseLeadPrice = (lead) => {
  const text = lead?.ai_quote;
  if (!text || typeof text !== "string") return null;

  let suggested = null;
  let rangeLow = null;
  let rangeHigh = null;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    const low = line.toLowerCase();
    const matches = [...line.matchAll(PRICE_NUM_RE)].map((m) => m[1]);

    if (low.startsWith("suggested price") && matches.length >= 1) {
      suggested = toNumber(matches[0]);
    } else if (
      (low.startsWith("price range") || low.startsWith("estimated price range")) &&
      matches.length >= 2
    ) {
      rangeLow = toNumber(matches[0]);
      rangeHigh = toNumber(matches[1]);
    }
  }

  if (suggested != null) return suggested;
  if (rangeLow != null && rangeHigh != null) return (rangeLow + rangeHigh) / 2;
  return null;
};

// Returns the dollar value we attribute to a single lead for the Potential
// Revenue card. Priority:
//   1. Parsed AI-quote number (existing behavior)
//   2. Per-service-type default
//   3. Global fallback
// Any lead with an open status contributes — no more "generate quotes to see
// your revenue" dead-end.
export const estimateLeadValue = (lead) => {
  const quoted = parseLeadPrice(lead);
  if (quoted != null) return quoted;
  return estimateServiceTypePrice(lead?.service_type);
};

export const summarizeEarnings = (leads) => {
  const empty = {
    total: 0,
    leadCount: 0,
    byScore: { hot: 0, warm: 0, cold: 0 },
  };
  if (!Array.isArray(leads)) return empty;

  let total = 0;
  let leadCount = 0;
  const byScore = { hot: 0, warm: 0, cold: 0 };

  for (const lead of leads) {
    if (!lead || !OPEN_STATUSES.has(lead.status)) continue;
    const price = estimateLeadValue(lead);
    if (!Number.isFinite(price) || price <= 0) continue;

    total += price;
    leadCount += 1;

    const label = String(lead.lead_score_label || "").toLowerCase();
    if (label === "hot") byScore.hot += price;
    else if (label === "warm") byScore.warm += price;
    else if (label === "cold") byScore.cold += price;
  }

  return { total, leadCount, byScore };
};

// Lead-score breakdown (counts, not dollars). Used by the Hot Leads card.
// Unknown/missing labels are classified as "cold" so the UI stays stable.
export const summarizeLeadScores = (leads) => {
  const out = { hot: 0, warm: 0, cold: 0 };
  if (!Array.isArray(leads)) return out;
  for (const lead of leads) {
    if (!lead) continue;
    const label = String(lead.lead_score_label || "").toLowerCase();
    if (label === "hot") out.hot += 1;
    else if (label === "warm") out.warm += 1;
    else out.cold += 1;
  }
  return out;
};

// Pipeline funnel counts — used by the Lead progress card. Each later stage
// also counts the leads that have already advanced past it (a booked lead
// was, by definition, contacted and quoted too).
export const summarizeConversion = (leads) => {
  const out = { total: 0, contacted: 0, quoted: 0, booked: 0 };
  if (!Array.isArray(leads)) return out;
  for (const lead of leads) {
    if (!lead) continue;
    out.total += 1;
    const s = String(lead.status || "").toLowerCase();
    if (["contacted", "quoted", "booked", "completed"].includes(s)) out.contacted += 1;
    if (["quoted", "booked", "completed"].includes(s)) out.quoted += 1;
    if (["booked", "completed"].includes(s)) out.booked += 1;
  }
  return out;
};

export const formatCurrency = (amount) => {
  const n = Number(amount) || 0;
  return `$${Math.round(n).toLocaleString("en-US")}`;
};
